import TableContainer from "Common/TableContainer";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import CopyToClipboard from "react-copy-to-clipboard";
import snackbarUtil from "Utils/snackBarUtil";
import image from "../../assets/images/auth/maintenance.png";
import { authServices } from "Utils/auth/services";
import TableEntry from "Common/TableEntry";
import { ListPagination } from "Common/ListPagination";
import { baseURL } from "Utils/baseUrl";
import { useParams } from "react-router-dom";

interface Props {
  filterSubmit: () => void;
  getAllDeposit: (filters?: any) => Promise<void>
}

type ChildRef = {
  callChildFunction: () => void;
};

const DepositTable = forwardRef(({props, ref, getAllDeposit}:any) => {
  const { depositId } = useParams();
  const userId = localStorage.getItem("userId");
  const [limit, setLimit] = useState<number>(50);
  const [page, setPage] = useState<number>(1);
  const [depositData, setDepositData] = useState<DepositData[]>([]);
  const [listTotal, setListTotal] = useState<ProfitLossTotal>({
    total: 0,
    page: 0,
  });

  const [showImage, setImage] = useState(false);
  const [showSetting, setSetting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showRemark, setRemark] = useState("Accept");
  const [imageLink, setImageLink] = useState<string>("");
  const [selectedDeposit, setSelectedDeposit] = useState<string>("");
  const [selectedReason, setSelectedReason] = useState("");
  const [utr, setUtr] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [isLoad, setLoad] = useState<boolean>(false);

  const toggleImage = (image?: string, id?: string, utr?: string) => {
    setImage(!showImage);
    setImageLink(image || "");
    setSelectedDeposit(id || "");
    setUtr(utr || "");
  };
  const toggleSetting = (id?: string, utr?: string) => {
    setSetting(!showSetting);
    setSelectedDeposit(id || "");
    setUtr(utr || "");
  };
  const toggleRemark = (label: any) => {
    setRemark(label);
  };

  const handleCopy = () => {
    setCopied(true);
    snackbarUtil.success("Copy Successfull");
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedReason(event.target.value);
  };

  const handleUTRChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUtr(event.target.value);
  };

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAmount(event.target.value);
  };

  const childFunction = () => {
    getPendingDeposit({});
  };

  useImperativeHandle(ref, () => ({
    callChildFunction: childFunction,
  }));

  const getPendingDeposit = async (filters: any = {}) => {
    const { response } = await authServices.getPendingDeposit({
      user_id: userId || "",
      status: "PENDING",
      statement_type: "DEPOSIT_REQUEST",
      search: {
        ...(username && { user_name: username }),
        ...(amount && { amount: amount }),
      },
      limit: limit,
      page: page,
      ...filters,
    });
    if (response?.status && response?.gettransactionListCrdr) {
      setDepositData(response?.gettransactionListCrdr?.data?.transactions);
      setListTotal({
        total:
          response?.gettransactionListCrdr?.data?.metadata?.total /
          (filters?.limit || limit) || 0,
        page: response?.gettransactionListCrdr?.data?.metadata?.page || 1,
      });
      
    } else {
      setDepositData([]);
      setListTotal({
        total: 0,
        page: 1,
      });
    }
  };

  useEffect(() => {
    getPendingDeposit({});
    const intervalId = setInterval(() => {
      getPendingDeposit({});
    }, 20000);

    return () => clearInterval(intervalId);
  }, []);

  const submitFilter = () => {
    getPendingDeposit({
      search: {
        ...(username && { user_name: username }),
        ...(amount && { amount: amount }),
      },
    });
  };

  const clearFilter = () => {
    setUsername("");
    setAmount("");
    getPendingDeposit({
      search: {},
    });
  };

  const handlePageFilter = (page: number, limit: number) => {
    setPage(page);
    setLimit(limit);

    getPendingDeposit({
      search: {
        ...(username && { user_name: username }),
        ...(amount && { amount: amount }),
      },
      page: page,
      limit: limit,
    });
  };

  const rejectDeposit = async () => {
    if (!selectedReason) {
      snackbarUtil.error("Please select Reason ");
      return;
    }
    const { response } = await authServices.rejectDeposit({
      user_id: userId || "",
      remark: selectedReason,
      statement_id: selectedDeposit,
    });
    if (response?.status) {
      snackbarUtil.success(response?.msg);
      getPendingDeposit();
      // toggleSetting();
      getAllDeposit({});
      setSetting(false);
      setImage(false);
      props?.filterSubmit();
    } else {
      const errorMessage =
        response?.msg || "An error occurred. Please try again.";
      snackbarUtil.error(errorMessage);
    }
  };

  const acceptDeposit = async () => {
    const { response } = await authServices.acceptDeposit({
      crdr: 1,
      reference_no: utr,
      statement_id: selectedDeposit,
    });
    if (response?.status) {
      snackbarUtil.success(response?.msg);
      getPendingDeposit();
      // toggleSetting();
      getAllDeposit({});
      setSetting(false);
      setImage(false);
      props?.filterSubmit();
    } else {
      const errorMessage =
        response?.msg || "An error occurred. Please try again.";
      snackbarUtil.error(errorMessage);
    }
  };

  function formatTimeDifference(targetDateTime: string): string {
    const now = new Date();
    const targetDate = new Date(targetDateTime);
    const diffInMs = Math.abs(targetDate.getTime() - now.getTime());

    const days = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffInMs % (1000 * 60)) / 1000);

    return `${days} days, ${hours} hours, ${minutes} Min ${seconds} Sec`;
  }

  function formatDateTime(dateString: string): string {
    const date = new Date(dateString);

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours() % 12 || 12;
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    const ampm = date.getHours() >= 12 ? "PM" : "AM";

    return `${day}-${month}-${year}, ${hours}:${minutes}:${seconds} ${ampm}`;
  }


  const deposit = useMemo(() => {
    if (!depositData || !Array.isArray(depositData)) {
      return [];
    }
    return depositData.map((items, index: number) => ({
      id: items?._id,
      username: items?.user_name,
      type: "Deposit",
      utr: items?.user_reference_no,
      tDate: formatDateTime(items?.created_at),
      rAmount: items?.amount,
      crypto: "",
      bankName:
        (items?.payment_deatails[0]?.method_name?.toLowerCase()) != "bank"
          ? items?.payment_deatails[0]?.method_name
          : items?.payment_deatails[0]?.bank_name,
      holderName: items?.payment_deatails[0]?.bank_holder_name,
      ifsc_code: items?.payment_deatails[0]?.ifsc_code,
      accountNo:
        (items?.payment_deatails[0]?.method_name?.toLowerCase()) != "bank"
          ? items?.payment_deatails[0]?.others
          : items?.payment_deatails[0]?.account_no,
      status: "Pending",
      updatedTime: items?.created_at && formatTimeDifference(items?.created_at),
      image: items?.self_host
        ? baseURL + "/" + items?.images
        : items?.images,
    }));
  }, [depositData]);


  const columns = useMemo(
    () => [
      {
        header: "Username",
        accessorKey: "username",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <>
              <span>{cell?.row?.original?.username}</span>
              <CopyToClipboard
                text={cell?.row?.original?.username}
                onCopy={handleCopy}
              >
                <i className="ms-1 align-middle ri-file-copy-line cursor-pointer"></i>
              </CopyToClipboard>
            </>
          );
        },
      },
      {
        header: "Type",
        accessorKey: "type",
        enableColumnFilter: false,
      },
      {
        header: "UTR No.",
        accessorKey: "utr",
        enableColumnFilter: false,
      },
      {
        header: "Transaction Date",
        accessorKey: "tDate",
        enableColumnFilter: false,
      },
      {
        header: "Request Amount",
        accessorKey: "rAmount",
        enableColumnFilter: false,
      },
      {
        header: "Crypto",
        accessorKey: "crypto",
        enableColumnFilter: false,
      },
      {
        header: "Bank Name",
        accessorKey: "bankName",
        enableColumnFilter: false,
      },
      {
        header: "Holder Name",
        accessorKey: "holderName",
        enableColumnFilter: false,
      },
      {
        header: "IFSC",
        accessorKey: "ifsc_code",
        enableColumnFilter: false,
      },
      {
        header: "Account No.",
        accessorKey: "accountNo",
        enableColumnFilter: false,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
      },
      {
        header: "Updated Time",
        accessorKey: "updatedTime",
        enableColumnFilter: false,
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <div>
              <Button
                className="btn-sm"
                variant="subtle-primary"
                onClick={() => toggleImage(cell?.row?.original?.image, cell?.row?.original?.id,
                  cell?.row?.original?.utr)}
              >
                <i className="fs-md align-middle ri-image-line"></i>
              </Button>
              <Button
                className="btn-sm ms-2"
                variant="subtle-dark"
                onClick={() =>
                  toggleSetting(
                    cell?.row?.original?.id,
                    cell?.row?.original?.utr
                  )
                }
              >
                <i className="fs-md align-middle ri-settings-4-line"></i>
              </Button>
              {/* <CopyToClipboard text={"Bank Details"} onCopy={handleCopy}>
                <Button className="btn-sm ms-2" variant="subtle-info">
                  <i className="fs-md align-middle ri-file-copy-line"></i>
                </Button>
              </CopyToClipboard> */}
            </div>
          );
        },
      },
    ],
    []
  );

  const selectedIndex = deposit.findIndex((item) => item.id === selectedDeposit);

  const changeDepositItem = (type: string) => {
    const newData = deposit[type === 'next' ? selectedIndex + 1 : selectedIndex - 1]
    setImageLink(newData.image || "");
    setSelectedDeposit(newData.id || "");
    setUtr(newData.utr || "");
  }

  useEffect(() => {
    if (!isLoad) {
      const selectedIndex = deposit.findIndex((item) => item.id === depositId);

      const newData = deposit[selectedIndex];
      setImageLink(newData?.image || "");
      setSelectedDeposit(newData?.id || "");
      setUtr(newData?.utr || "");
      setImage(newData ? true : false);
      setLoad(newData ? true : false);
    }
  }, [depositId, deposit, isLoad])

  return (
    <>
      <Card>
        <Card.Header className="border-bottom pb-3">
          <Row className="align-items-end g-3">
            <Col lg={3}>
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                value={username}
                placeholder="Enter Username"
                onChange={handleUsernameChange}
              />
            </Col>
            <Col lg={3}>
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                value={amount}
                placeholder="Enter Amount"
                onChange={handleAmountChange}
              />
            </Col>
            <Col lg={3}>
              <Button className="me-2" onClick={submitFilter}>
                Search
              </Button>
              <Button variant="subtle-primary" onClick={clearFilter}>
                Clear
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <TableContainer
            columns={columns || []}
            data={deposit || []}
            isBordered={false}
            customPageSize={limit}
            isPagination={false}
            tableClass="table-centered align-middle table-nowrap table-striped mb-3"
            theadClass="text-muted table-light"
            SearchPlaceholder="Search Products..."
          />

          <nav aria-label="Page navigation" className="px-3 d-flex">
            <TableEntry
              limit={limit}
              handlelimitFilter={(value: number) =>
                handlePageFilter(page, value)
              }
            />
            <ListPagination
              listTotal={listTotal}
              handlePageFilter={(value: number) =>
                handlePageFilter(value, limit)
              }
            />
          </nav>
        </Card.Body>
      </Card>

      <Modal
        show={showImage}
        onHide={toggleImage}
        className="zoomIn"
        scrollable
      >
        <Modal.Header className="modal-title fw-bold pb-2">
          <div>Screen Shot</div>
          <Button variant="light btn-sm" onClick={() => toggleImage()}>
            <i className="ri-close-line fs-xl align-middle"></i>
          </Button>
        </Modal.Header>
        <Modal.Body className="pt-1">
          <img src={imageLink} alt="Image" className="w-100 mb-5" />

          <div className="d-flex justify-content-center">
            <Button
              variant="subtle-success"
              className="d-flex flex-column py-3 w-25 mx-3 align-items-center"
              onClick={() => toggleRemark("Accept")}
            >
              <i
                className="ri-check-line align-middle"
                style={{ fontSize: "30px" }}
              ></i>
              <span className="fs-xl">Accept</span>
            </Button>
            <Button
              variant="subtle-danger"
              className="d-flex flex-column py-3 w-25 mx-3 align-items-center"
              onClick={() => toggleRemark("Reject")}
            >
              <i
                className="ri-close-line align-middle"
                style={{ fontSize: "30px" }}
              ></i>
              <span className="fs-xl">Reject</span>
            </Button>
          </div>
          <div className="mt-3 mx-auto w-75">
            {showRemark === "Accept" && (
              <div className="input-group">
                <div className="input-group-text">UTR No.</div>
                <Form.Control
                  type="text"
                  value={utr}
                  onChange={handleUTRChange}
                />
              </div>
            )}
            {showRemark === "Reject" && (
              <div className="input-group">
                <div className="input-group-text">Remark</div>
                <Form.Select
                  value={selectedReason}
                  onChange={handleSelectChange}
                >
                  <option value="">Please Select</option>
                  <option value="Tech Glitch">Tech Glitch</option>
                  <option value="Reference Number Missing">
                    Reference Number Missing
                  </option>
                  <option value="Edited Payment Slip">
                    Edited Payment Slip
                  </option>
                  <option value="Invalid Bank">Invalid Bank</option>
                  <option value="Payment Late Attached / Old Payment">
                    Payment Late Attached / Old Payment
                  </option>
                  <option value="Payment Not Updated">
                    Payment Not Updated
                  </option>
                  <option value="Used in Same User">Used in Same User</option>
                  <option value="Used in Other User/Site">
                    Used in Other User/Site
                  </option>
                  <option value="UTR & Account Didn't Match">
                    UTR & Account Didn't Match
                  </option>
                  <option value="Insufficient Balance">
                    Insufficient Balance
                  </option>
                </Form.Select>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button variant="subtle-dark" onClick={() => changeDepositItem('prev')} disabled={!(selectedIndex !== -1 && selectedIndex > 0)}>
            Prev
          </Button>
          <Button variant="subtle-dark" onClick={() => changeDepositItem('next')} disabled={!(selectedIndex !== -1 && selectedIndex < deposit.length - 1)}>
            Next
          </Button>
          <Button
            onClick={() =>
              showRemark === "Reject" ? rejectDeposit() : acceptDeposit()
            }
          >
            Update
          </Button>
          <Button variant="subtle-dark" onClick={() => toggleImage()}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSetting}
        onHide={toggleSetting}
        className="zoomIn"
        scrollable
      >
        <Modal.Header className="modal-title fw-bold">
          Username
          <Button variant="light btn-sm" onClick={() => toggleSetting()}>
            <i className="ri-close-line fs-xl align-middle"></i>
          </Button>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-center">
            <Button
              variant="subtle-success"
              className="d-flex flex-column py-3 w-25 mx-3 align-items-center"
              onClick={() => toggleRemark("Accept")}
            >
              <i
                className="ri-check-line align-middle"
                style={{ fontSize: "30px" }}
              ></i>
              <span className="fs-xl">Accept</span>
            </Button>
            <Button
              variant="subtle-danger"
              className="d-flex flex-column py-3 w-25 mx-3 align-items-center"
              onClick={() => toggleRemark("Reject")}
            >
              <i
                className="ri-close-line align-middle"
                style={{ fontSize: "30px" }}
              ></i>
              <span className="fs-xl">Reject</span>
            </Button>
          </div>
          <div className="mt-3 mx-auto w-75">
            {showRemark === "Accept" && (
              <div className="input-group">
                <div className="input-group-text">UTR No.</div>
                <Form.Control
                  type="text"
                  value={utr}
                  onChange={handleUTRChange}
                />
              </div>
            )}
            {showRemark === "Reject" && (
              <div className="input-group">
                <div className="input-group-text">Remark</div>
                <Form.Select
                  value={selectedReason}
                  onChange={handleSelectChange}
                >
                  <option value="">Please Select</option>
                  <option value="Tech Glitch">Tech Glitch</option>
                  <option value="Reference Number Missing">
                    Reference Number Missing
                  </option>
                  <option value="Edited Payment Slip">
                    Edited Payment Slip
                  </option>
                  <option value="Invalid Bank">Invalid Bank</option>
                  <option value="Payment Late Attached / Old Payment">
                    Payment Late Attached / Old Payment
                  </option>
                  <option value="Payment Not Updated">
                    Payment Not Updated
                  </option>
                  <option value="Used in Same User">Used in Same User</option>
                  <option value="Used in Other User/Site">
                    Used in Other User/Site
                  </option>
                  <option value="UTR & Account Didn't Match">
                    UTR & Account Didn't Match
                  </option>
                  <option value="Insufficient Balance">
                    Insufficient Balance
                  </option>
                </Form.Select>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button
            onClick={() =>
              showRemark === "Reject" ? rejectDeposit() : acceptDeposit()
            }
          >
            Update
          </Button>
          <Button variant="subtle-dark" onClick={() => toggleSetting()}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

export default DepositTable;
